import React from "react";
import { useNavigate } from "react-router-dom";
import { Box, Paper, Stack, Typography } from "@mui/material";
import {
  EditOutlined as ScratchIcon,
  ContentCopyOutlined as CopyIcon,
} from "@mui/icons-material";
import { ROUTES } from "@/routes/config";
import PageLayout from "@/components/PageLayout";

interface ChoiceCardProps {
  icon: React.ReactElement;
  title: string;
  description: string;
  onClick: () => void;
}

const ChoiceCard: React.FC<ChoiceCardProps> = ({
  icon,
  title,
  description,
  onClick,
}) => (
  <Paper
    elevation={0}
    onClick={onClick}
    sx={{
      p: 3,
      border: "1px solid",
      borderColor: "divider",
      bgcolor: "background.paper",
      cursor: "pointer",
      transition: "border-color 0.15s, box-shadow 0.15s",
      "&:hover": {
        borderColor: "primary.main",
        boxShadow: 1,
      },
    }}
  >
    <Stack spacing={1.5}>
      <Box sx={{ color: "text.secondary" }}>{React.cloneElement(icon as React.ReactElement<Record<string, unknown>>, { fontSize: "large" })}</Box>
      <Typography variant="h6" fontWeight={700}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
    </Stack>
  </Paper>
);

const CreatePortalChooser: React.FC = () => {
  const navigate = useNavigate();

  return (
    <PageLayout
      title="Create a new portal"
      subtitle="How would you like to get started?"
      backTo={ROUTES.PORTALS}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 2.5,
          maxWidth: 900,
        }}
      >
        <ChoiceCard
          icon={<ScratchIcon />}
          title="From Scratch"
          description="Start with a blank setup and configure every detail of the new portal yourself."
          onClick={() => navigate(ROUTES.CREATE_PORTAL_SCRATCH)}
        />
        <ChoiceCard
          icon={<CopyIcon />}
          title="From Existing Portal"
          description="Pick an existing portal and copy its configuration as a starting point. You can edit every value before the new portal is created. Content (agents, CMS pages, files) is imported separately after the new portal is active."
          onClick={() => navigate(ROUTES.CREATE_PORTAL_COPY)}
        />
      </Box>
    </PageLayout>
  );
};

export default CreatePortalChooser;
